import React, { useContext } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { getDesignTokens, setDocumentUiVariables } from '@eposnow/ui-core';
import { UIContext } from './UIContext';


declare module '@mui/material/styles' {
  interface TypographyVariants {
    eventTitle: React.CSSProperties;
    pieBarText: React.CSSProperties;
    tooltipText: React.CSSProperties;
    lineBarText: React.CSSProperties;
    subtitle3: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    eventTitle?: React.CSSProperties;
    pieBarText?: React.CSSProperties;
    tooltipText?: React.CSSProperties;
    lineBarText?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
  }

  interface SimplePaletteColorOptions {
    light?: string;
    main: string;
    dark?: string;
    contrastText?: string;
    '160p'?: string;
    '190p'?: string;
  }

  interface PaletteColor {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
    '160p'?: string;
    '190p'?: string;
  }

  interface TypeText {
    primary: string;
    secondary: string;
    disabled: string;
    contrast?: string;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    blue: PaletteColor;
    darkBlue: PaletteColor;
    cyan: PaletteColor;
    teal: PaletteColor;
    lightGreen: PaletteColor;
    lime: PaletteColor;
    yellow: PaletteColor;
    orange: PaletteColor;
    deepOrange: PaletteColor;
    fucsia: PaletteColor;
    purple: PaletteColor;
    amber: PaletteColor;
    greyed: PaletteColor;
    greyIcon: PaletteColor;
    darkerGrey: PaletteColor;
  }
  interface PaletteOptions {
    blue?: any;
    darkBlue?: any;
    cyan?: any;
    teal?: any;
    lightGreen?: any;
    lime?: any;
    yellow?: any;
    orange?: any;
    deepOrange?: any;
    fucsia?: any;
    purple?: any;
    amber?: any;
    greyed?: any;
    greyIcon?: any;
    darkerGrey: any;

  }
}


declare module '@mui/material/index' {
  interface Color {
    '4p'?: string;
    '8p'?: string;
    '12p'?: string;
    '16p'?: string;
    '18p'?: string;
    '23p'?: string;
    '26p'?: string;
    '30p'?: string;
    '50p'?: string;
    '54p'?: string;
    '56p'?: string;
    '65p'?: string;
    '70p'?: string;
    '87p'?: string;
    '0dp'?: string;
    '1dp'?: string;
    '2dp'?: string;
    '4dp'?: string;
    '8dp'?: string;
    '12dp'?: string;
    '16dp'?: string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    eventTitle: true;
    subtitle3: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    "greyed": true;
  }
}

const ThemeContext = ({ children }: { children: React.ReactNode }) => {
  const { colorMode } = useContext(UIContext);
  const designTokens = getDesignTokens(colorMode ?? 'light');
  if(designTokens.components) {
    designTokens.components.MuiSwitch = {
      styleOverrides: {
        root: {
          ":hover": {
            boxShadow: '0 0 0 1.5px rgba(20, 20, 20, 0.9)',
            borderRadius: '10%',
          },
        },
      },
    }
  }

  designTokens.palette = {
    ...designTokens.palette,
    blue: {
          main: '#007DFF',
          light: '#EBF1FC',
          dark: '#1976D2',
          contrastText: '#0D47A1'
    },
    darkBlue:{
      main: '#0D223E',
      light: '#1A2A4D'
    },
    cyan: {
      main: '#00BCD4',
      light: '#E0F7FA',
      dark: '#4DD0E1',
      contrastText: '#006064'
    },
    teal: {
      main: '#009688',
      light: '#E0F2F1',
      dark: '#4DB6AC',
      contrastText: '#004D40'
    },
    lightGreen: {
      main: '#8BC34A',
      light: '#F1F8E9',
      dark: '#AED581',
      contrastText: '#33691E'
    },
    lime: {
      main: '#CDDC39',
      light: '#F9FBE7',
      dark: '#DCE775',
      contrastText: '#827717'
    }, 
    yellow: {
      main: '#FFEB3B',
      light: '#FFFDE7',
      dark: '#FFF176',
      contrastText: '#F57F17'
    }, 
    orange: {
      main: '#FF9800',
      light: '#FFF3E0',
      dark: '#FFB74D',
      contrastText: '#E65100'
    },
    deepOrange: {
      main: '#FF5722',
      light: '#FBE9E7',
      dark: '#FF8A65',
      contrastText: '#BF360C'
    },
    fucsia: {
      main: '#E91E63',
      light: '#FCE4EC',
      dark: '#F06292',
      contrastText: '#880E4F'
    },
    purple: {
      main: '#9C27B0',
      light: '#F3E5F5',
      dark: '#BA68C8',
      contrastText: '#4A148C'
    },
    amber:{
      main: '#FF9800',
      light: '#FFF4E5'
    },
    greyed:{
      main: '#595959',
      light: '#595959'
    },
    greyIcon:{
      main: '#757575'
    },
    darkerGrey:{
      main: '#607D8B',
      light: '#90A4AE'
    }
  }

  designTokens.typography = {
    ...designTokens.typography,
    pieBarText: {
      fontWeight: 600,
      fontSize: '16px'
    },
    tooltipText: {
      fontWeight: 600,
      fontSize: '14px'
    },
    lineBarText: {
      fontWeight: 600,
      fontSize: '12px'
    },
    subtitle3: {
      fontFamily: 'Open Sans',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '130%',
      letterSpacing: '0.2px',
      verticalAlign: 'middle',
    },
  }
  setDocumentUiVariables(colorMode);

  return <ThemeProvider theme={createTheme(designTokens)}>{children}</ThemeProvider>;
}

export default ThemeContext;
